/**
 * carousel.js — shared Swiper init module (vanilla ES2017+, no jQuery).
 *
 * Data-attribute contract (sections rely on this — keep in sync with
 * docs/carousels/PLAN-oracle-ruling.md §2):
 *
 *   data-carousel                 Marks an element as a MAIN carousel root.
 *                                 Only elements carrying this attribute are
 *                                 picked up by the generic scan below.
 *   data-carousel-scope            Opt-in scope marker for resolveScope().
 *                                 When present on an ancestor, it is tried
 *                                 BEFORE the [data-section-id]/.shopify-section/
 *                                 section/document chain. Needed when a
 *                                 section can render multiple independent
 *                                 carousel pairs (e.g. a repeatable block
 *                                 with its own control/nav slave) so that
 *                                 data-carousel-control/-nav/-thumbs resolve
 *                                 within that block, not the first
 *                                 section-wide match (Wave 2.3,
 *                                 article-back-slider pair).
 *   data-carousel-initialized     Set by this module once a root has been
 *                                 turned into a Swiper instance. Prevents
 *                                 double-init on repeated scans.
 *   data-swiper='{...}'           JSON Swiper config for the element it is
 *                                 declared on (main carousel OR a thumbs/
 *                                 control slave — each element reads its own).
 *   data-carousel-min-width="N"   Gate: construct the Swiper only when
 *                                 matchMedia(`(min-width: Npx)`) matches;
 *                                 destroy(true, true) when it no longer does.
 *                                 Listens for viewport changes and re-applies.
 *   data-carousel-thumbs="<sel>"  Selector (resolved within the same
 *                                 [data-carousel-root]/section scope first,
 *                                 falling back to document) for a thumbnail
 *                                 nav element. That element is initialized
 *                                 first, as its OWN Swiper instance (reading
 *                                 its own data-swiper), then passed into the
 *                                 main config as thumbs:{ swiper: instance }.
 *   data-carousel-control="<sel>[,<sel>]"
 *                                 Comma-separated selector list of slave
 *                                 elements. Each is initialized as its own
 *                                 Swiper (own data-swiper), then the main
 *                                 config gets controller:{ control:[...] }.
 *   data-carousel-nav="<sel>"     Selector for a nav container. Clicks on
 *                                 its [data-index] descendants call
 *                                 swiper.slideTo(parseInt(dataIndex) - 1)
 *                                 (delegated listener on the container).
 *   data-carousel-nav-active-class="<class>"
 *                                 Opt-in, used alongside data-carousel-nav.
 *                                 On the main swiper's slideChange, the
 *                                 active source index is computed
 *                                 (swiper.realIndex if params.loop, else
 *                                 swiper.activeIndex) and, 1-based
 *                                 (index + 1), matched against the
 *                                 [data-index] descendants of the nav
 *                                 container: <class> is toggled onto the
 *                                 single matching one, removed from the
 *                                 rest. Looked up by index on every call
 *                                 (never a cached node ref) — loop clones
 *                                 shift DOM order. data-index is always
 *                                 1-based (shared convention with the
 *                                 data-carousel-nav click path above).
 *   data-carousel-focus-on-select (boolean) Slick focusOnSelect parity for a
 *                                 MAIN (non-thumbs) carousel: clicking any
 *                                 slide navigates to it. Wired on the main
 *                                 swiper's own tap event (Wave 2.4) — mirrors
 *                                 the existing thumbs tap reimplementation
 *                                 below (slideToLoop when looped and the
 *                                 clicked slide carries a clone's source
 *                                 index, else slideTo(clickedIndex)).
 *
 * Document-level events (cart-drawer reco carousel, Wave 2.2, D3d/D4):
 *
 *   cart:reco-carousel-updated   Dispatched by theme-apm.js's renderCart()
 *                                after a successful /cart?view=reco-carousel
 *                                fetch, detail:{html}. Parses html (DOMParser)
 *                                and compares the incoming .swiper-slide markup
 *                                against the last applied signature. If it is
 *                                unchanged the DOM is left completely alone —
 *                                rebuilding would replace every <img> node and
 *                                make the images visibly flicker on each cart
 *                                update. Otherwise it replaces
 *                                #reco-products-slider-content's .swiper-wrapper
 *                                innerHTML with the fresh markup, destroys and
 *                                reconstructs that Swiper instance
 *                                (destroyCarousel(el,false) + constructMain(el))
 *                                so slide count/nav stay in sync with the cart,
 *                                and restores the shopper's slide position.
 *   cart:view-toggled            Dispatched by theme-apm.js's updateCartView()
 *                                (replaces the old unscoped
 *                                $(".slick-arrow--right").click() nudge). If
 *                                #reco-products-slider-content has a live
 *                                swiper instance, calls swiper.update() to
 *                                recompute sizes after the drawer's
 *                                mobile/desktop visibility toggle.
 *
 * IMPORTANT: thumbs/control slave elements must NOT carry data-carousel
 * themselves (only class="swiper" + data-swiper) — otherwise the generic
 * [data-carousel] scan would try to initialize them a second time as their
 * own independent main carousel.
 *
 * el.swiper holds the live Swiper instance once initialized.
 */

(function () {
  "use strict";

  var PREFERS_REDUCED_MOTION =
    window.matchMedia && window.matchMedia("(prefers-reduced-motion: reduce)").matches;

  function parseConfig(el) {
    var raw = el.dataset.swiper || "{}";
    var config;
    try {
      config = JSON.parse(raw);
    } catch (e) {
      config = {};
    }

    if (PREFERS_REDUCED_MOTION) {
      delete config.autoplay;
      config.speed = 0;
    }

    guardLoop(el, config);

    return config;
  }

  // Swiper needs at least slidesPerView + slidesPerGroup real slides to build
  // loop mode. Below that it logs "The number of slides is not enough for loop
  // mode" and silently disables looping — but only after autoplay and the nav
  // have been wired against a loop that does not exist, which leaves the
  // carousel half-working rather than cleanly non-looping.
  //
  // Slide counts here are data-driven (cart suggestions, in-stock products), so
  // a carousel can cross that threshold in production without anything in the
  // theme changing. Decide up front instead: if there are not enough slides,
  // turn loop off ourselves so Swiper initializes a coherent finite carousel.
  //
  // Uses the LARGEST slidesPerView across the base config and every breakpoint,
  // so a carousel cannot loop correctly at one width and break at another. The
  // trade-off is that a strip which could loop on mobile stops looping there
  // too — predictable beats intermittently broken.
  function maxSlidesPerView(config) {
    var values = [];
    var seenAuto = false;

    var collect = function (value) {
      if (value === "auto") seenAuto = true;
      else if (typeof value === "number") values.push(value);
    };

    collect(config.slidesPerView);
    if (config.breakpoints) {
      Object.keys(config.breakpoints).forEach(function (key) {
        var bp = config.breakpoints[key];
        if (bp) collect(bp.slidesPerView);
      });
    }

    // slidesPerView:auto depends on rendered widths, so it cannot be resolved
    // before init. Leave those to Swiper rather than guessing.
    if (seenAuto) return null;
    return values.length ? Math.max.apply(Math, values) : 1;
  }

  function guardLoop(el, config) {
    if (!config.loop) return;

    var wrapper = el.querySelector(".swiper-wrapper");
    if (!wrapper) return;

    // Direct children only: a nested carousel's slides must not be counted.
    var count = 0;
    for (var i = 0; i < wrapper.children.length; i++) {
      if (wrapper.children[i].classList.contains("swiper-slide")) count++;
    }

    var perView = maxSlidesPerView(config);
    if (perView === null) return;

    if (count < Math.ceil(perView) + (config.slidesPerGroup || 1)) {
      config.loop = false;
    }
  }

  function resolveScope(el) {
    return (
      el.closest("[data-carousel-scope]") ||
      el.closest("[data-section-id]") ||
      el.closest(".shopify-section") ||
      el.closest("section") ||
      document
    );
  }

  function resolveSelector(scope, selector) {
    if (!selector) return null;
    var found = scope.querySelector(selector);
    if (!found && scope !== document) found = document.querySelector(selector);
    return found;
  }

  function initSlave(el) {
    if (!el || el.dataset.carouselInitialized !== undefined) {
      return el && el.swiper ? el.swiper : el ? el.swiper : null;
    }
    var config = parseConfig(el);
    el.swiper = new window.Swiper(el, config);
    el.dataset.carouselInitialized = "";
    return el.swiper;
  }

  // Slick parity: slick's position grid ends at the LAST GROUP START, but
  // Swiper appends an extra end-aligned snap point when the last group does
  // not fill the viewport (snapGrid.push(virtualSize - size) in updateSlides).
  // That extra snap lets users drag slightly past the last group (half-cards,
  // premature isEnd/arrow-hide). Opt-in via data-carousel-group-snap.
  function trimSnapGrid(swiper) {
    if (!swiper || swiper.destroyed) return;
    var group = swiper.params.slidesPerGroup || 1;
    if (swiper.params.loop || swiper.params.centeredSlides) return;
    var groups = Math.ceil(swiper.slides.length / group);
    if (groups > 0 && swiper.snapGrid.length > groups) {
      swiper.snapGrid = swiper.snapGrid.slice(0, groups);
      swiper.updateProgress();
      swiper.updateSlidesClasses();
    }
  }

  function bindNavActiveClass(mainEl, navSelector, activeClass) {
    var scopeEl = resolveScope(mainEl);
    var navEl = resolveSelector(scopeEl, navSelector);
    if (!navEl || !mainEl.swiper) return;

    var apply = function () {
      var swiper = mainEl.swiper;
      if (!swiper) return;
      var activeIndex = (swiper.params.loop ? swiper.realIndex : swiper.activeIndex) + 1;
      var items = navEl.querySelectorAll("[data-index]");
      items.forEach(function (item) {
        item.classList.toggle(activeClass, parseInt(item.dataset.index, 10) === activeIndex);
      });
    };

    mainEl.swiper.on("slideChange", apply);
    apply();
  }

  function bindNav(mainEl, navSelector) {
    // Delegated at scope level, NOT on a specific nav container: fade
    // carousels (hero) bake a copy of the nav into EVERY slide, and only the
    // active slide's copy is clickable (Swiper's fade CSS sets
    // pointer-events:none on inactive slides). Binding to the first copy
    // found would go dead as soon as the slide changes.
    var scopeEl = resolveScope(mainEl);

    var handler = function (event) {
      var dot = event.target.closest("[data-index]");
      if (!dot || !dot.closest(navSelector)) return;
      var index = parseInt(dot.dataset.index, 10);
      if (isNaN(index) || !mainEl.swiper) return;
      mainEl.swiper.slideTo(index - 1);
    };

    scopeEl.addEventListener("click", handler);
    return { el: scopeEl, handler: handler };
  }

  // Swiper resolves navigation.prevEl/nextEl selector STRINGS document-wide
  // (it only narrows to the instance when the arrow is a DESCENDANT of the
  // swiper root — ours are siblings). With two instances of the same section/
  // block on a page, both carousels would bind BOTH arrow sets. Resolve the
  // selectors here, within the carousel's scope, and pass live elements.
  function scopeNavigationEls(config, scope) {
    if (!config.navigation) return;
    ["prevEl", "nextEl"].forEach(function (key) {
      var sel = config.navigation[key];
      if (typeof sel !== "string") return;
      var found = resolveSelector(scope, sel);
      if (found) config.navigation[key] = found;
    });
  }

  function constructMain(el) {
    var config = parseConfig(el);
    var scope = resolveScope(el);
    var slaves = [];

    scopeNavigationEls(config, scope);

    var thumbsSelector = el.dataset.carouselThumbs;
    var thumbsWantsClickSlide = false;
    var thumbsInstance = null;
    if (thumbsSelector) {
      var thumbsEl = resolveSelector(scope, thumbsSelector);
      if (thumbsEl) {
        thumbsWantsClickSlide = parseConfig(thumbsEl).slideToClickedSlide === true;
        var thumbsSwiper = initSlave(thumbsEl);
        if (thumbsSwiper) {
          slaves.push(thumbsEl);
          thumbsInstance = thumbsSwiper;
          config.thumbs = Object.assign({}, config.thumbs, { swiper: thumbsSwiper });
        }
      }
    }

    var controlSelector = el.dataset.carouselControl;
    if (controlSelector) {
      var controlInstances = [];
      controlSelector.split(",").forEach(function (sel) {
        sel = sel.trim();
        if (!sel) return;
        var controlEl = resolveSelector(scope, sel);
        if (!controlEl) return;
        var controlSwiper = initSlave(controlEl);
        if (controlSwiper) {
          slaves.push(controlEl);
          controlInstances.push(controlSwiper);
        }
      });
      if (controlInstances.length) {
        config.controller = Object.assign({}, config.controller, { control: controlInstances });
      }
    }

    el.swiper = new window.Swiper(el, config);
    el.dataset.carouselInitialized = "";

    // Slick asNavFor parity (QA round 2): asNavFor was declared on BOTH
    // sliders (mutual sync), but the Controller config above only wires
    // master->slaves. Wire the reverse so swiping a control slave (e.g. the
    // article-back image pane) drives the master text pane too.
    if (controlSelector && controlInstances && controlInstances.length) {
      controlInstances.forEach(function (ci) {
        ci.controller.control = el.swiper;
      });
    }

    // Slick focusOnSelect parity for a MAIN carousel (Wave 2.4): click any
    // slide to navigate to it. Same clickedSlide/clickedIndex API as the
    // thumbs tap reimpl below, bound to the main swiper's own tap event.
    if (el.hasAttribute("data-carousel-focus-on-select")) {
      el.swiper.on("tap", function (s) {
        if (s.params.loop && s.clickedSlide) {
          var real = s.clickedSlide.getAttribute("data-swiper-slide-index");
          if (real !== null) {
            s.slideToLoop(parseInt(real, 10));
            return;
          }
        }
        if (typeof s.clickedIndex === "number" && s.clickedIndex >= 0) {
          s.slideTo(s.clickedIndex);
        }
      });
    }

    // Swiper's Thumbs module force-disables slideToClickedSlide on the thumbs
    // instance (it overwrites params at attach time). If the slave's own
    // data-swiper asked for it (slick centerMode+focusOnSelect parity: click
    // a thumb -> that thumb scrolls to center), reimplement it via tap.
    if (thumbsInstance && thumbsWantsClickSlide) {
      thumbsInstance.on("tap", function (s) {
        var i = s.clickedIndex;
        if (typeof i !== "number" || i < 0) return;
        if (s.params.loop && s.clickedSlide) {
          var real = s.clickedSlide.getAttribute("data-swiper-slide-index");
          if (real !== null) {
            s.slideToLoop(parseInt(real, 10));
            return;
          }
        }
        s.slideTo(i);
      });
    }

    if (el.hasAttribute("data-carousel-group-snap")) {
      var trim = function () {
        trimSnapGrid(el.swiper);
      };
      // slidesUpdated fires at the end of every updateSlides() (init,
      // resize, breakpoint) — i.e. every time Swiper rebuilds snapGrid.
      el.swiper.on("slidesUpdated resize", trim);
      trim();
    }

    var navBinding = null;
    var navSelector = el.dataset.carouselNav;
    if (navSelector) {
      navBinding = bindNav(el, navSelector);

      var navActiveClass = el.dataset.carouselNavActiveClass;
      if (navActiveClass) {
        bindNavActiveClass(el, navSelector, navActiveClass);
      }
    }

    el._carouselSlaves = slaves;
    el._carouselNav = navBinding;
  }

  function initWithMinWidth(el, minWidth) {
    var mql = window.matchMedia("(min-width: " + minWidth + "px)");

    var apply = function () {
      if (mql.matches) {
        if (!el.swiper) constructMain(el);
      } else if (el.swiper) {
        destroyCarousel(el, false);
      }
    };

    apply();

    var changeHandler = function () {
      apply();
    };
    if (mql.addEventListener) {
      mql.addEventListener("change", changeHandler);
    } else if (mql.addListener) {
      mql.addListener(changeHandler);
    }

    el._carouselMql = { mql: mql, handler: changeHandler };
    el.dataset.carouselInitialized = "";
  }

  function destroyCarousel(el, removeMarker) {
    if (el.swiper) {
      el.swiper.destroy(true, true);
      el.swiper = null;
    }

    if (el._carouselSlaves) {
      el._carouselSlaves.forEach(function (slaveEl) {
        if (slaveEl.swiper) {
          slaveEl.swiper.destroy(true, true);
          slaveEl.swiper = null;
        }
        delete slaveEl.dataset.carouselInitialized;
      });
      el._carouselSlaves = null;
    }

    if (el._carouselNav) {
      el._carouselNav.el.removeEventListener("click", el._carouselNav.handler);
      el._carouselNav = null;
    }

    if (removeMarker) {
      if (el._carouselMql) {
        var mql = el._carouselMql.mql;
        var handler = el._carouselMql.handler;
        if (mql.removeEventListener) {
          mql.removeEventListener("change", handler);
        } else if (mql.removeListener) {
          mql.removeListener(handler);
        }
        el._carouselMql = null;
      }
      delete el.dataset.carouselInitialized;
    }
  }

  function initCarousels(root) {
    root = root || document;
    if (!window.Swiper) return;

    var roots = root.querySelectorAll
      ? root.querySelectorAll("[data-carousel]:not([data-carousel-initialized])")
      : [];

    roots.forEach(function (el) {
      var minWidth = el.dataset.carouselMinWidth;
      if (minWidth) {
        initWithMinWidth(el, parseInt(minWidth, 10));
      } else {
        constructMain(el);
      }
    });
  }

  function onDomReady() {
    if (window.Swiper) {
      initCarousels(document);
    } else {
      window.addEventListener(
        "load",
        function () {
          initCarousels(document);
        },
        { once: true }
      );
    }
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", onDomReady);
  } else {
    onDomReady();
  }

  document.addEventListener("shopify:section:load", function (event) {
    initCarousels(event.target);
  });

  document.addEventListener("shopify:section:unload", function (event) {
    var els = event.target.querySelectorAll("[data-carousel][data-carousel-initialized]");
    els.forEach(function (el) {
      destroyCarousel(el, true);
    });
  });

  document.addEventListener("cart:reco-carousel-updated", function (event) {
    var el = document.getElementById("reco-products-slider-content");
    if (!el) return;

    var html = event.detail && event.detail.html;
    if (typeof html !== "string") return;

    var wrapper = el.querySelector(".swiper-wrapper");
    if (!wrapper) return;

    var doc = new DOMParser().parseFromString(html, "text/html");
    var slides = doc.body.querySelectorAll(".swiper-slide");

    // Every cart mutation re-renders the drawer, but the suggestions themselves
    // usually do not change (bumping a quantity leaves the same products). The
    // rebuild below discards every slide node and, because this carousel runs
    // loop:true, makes Swiper regenerate its duplicate slides too — so all the
    // <img> nodes are replaced and the browser repaints them from scratch.
    // Cached bytes do not prevent that: a brand new element still decodes and
    // paints, which is what shows up as the images flickering (and briefly
    // disappearing) on every cart update.
    //
    // So only touch the DOM when the server actually sent different slides.
    // Signature is built from the incoming markup, never from the live wrapper,
    // because the live wrapper also holds Swiper's loop clones.
    var signature = "";
    slides.forEach(function (slide) {
      signature += slide.outerHTML;
    });
    // Expando rather than dataset: this is a few KB of markup and dataset would
    // serialize all of it into a visible data- attribute on every cart update.
    if (el._recoSignature === signature) return;
    el._recoSignature = signature;

    var wasInitialized = !!el.swiper;
    var activeIndex = wasInitialized ? el.swiper.realIndex : 0;

    wrapper.innerHTML = "";
    slides.forEach(function (slide) {
      wrapper.appendChild(slide);
    });

    if (wasInitialized) {
      destroyCarousel(el, false);
    }
    constructMain(el);

    // Keep the shopper where they were rather than snapping back to the first
    // suggestion, when the new list is long enough to still have that slide.
    if (el.swiper && activeIndex > 0 && activeIndex < slides.length) {
      el.swiper.slideToLoop(activeIndex, 0);
    }
  });

  document.addEventListener("cart:view-toggled", function () {
    var el = document.getElementById("reco-products-slider-content");
    if (el && el.swiper) {
      el.swiper.update();
    }
  });

  document.addEventListener("shopify:block-select", function (event) {
    var blockEl = event.target;
    if (!blockEl.classList || !blockEl.classList.contains("swiper-slide")) return;
    var carouselEl = blockEl.closest("[data-carousel][data-carousel-initialized]");
    if (!carouselEl || !carouselEl.swiper) return;
    var slides = Array.prototype.slice.call(carouselEl.querySelectorAll(".swiper-slide"));
    var index = slides.indexOf(blockEl);
    if (index === -1) return;
    carouselEl.swiper.slideTo(index);
  });

  window.initCarousels = initCarousels;
})();
